import { useState } from 'react';
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleDown,
  FaAngleLeft,
  FaAngleRight,
  FaAngleUp,
} from 'react-icons/fa';
import { Box, BoxProps, Flex, FormErrorMessageProps } from '@chakra-ui/react';
import { FormikHelpers } from 'formik';
import { ListBoxProps } from 'components/UI/ListBox';
import { IconButton } from 'components/UI/IconButton';
import { IOption } from 'types';
import { FormErrorMessage } from 'components/UI/forms/FormErrorMessage';
import { Text } from 'components/UI/Text';
import { addItems, deleteItems } from 'utils/lists';
import { arrayMove } from '@dnd-kit/sortable';
import FormListSelectionBox from './FormListSelectionBox';

enum Direction {
  Up = 'Up',
  Down = 'Down',
}

export type FormListSelectionProps = Omit<ListBoxProps, 'name'> &
  Pick<FormikHelpers<unknown>, 'setFieldValue'> & {
    error?: string;
    errorProps?: FormErrorMessageProps;
    resultContainerProps?: BoxProps;
    hideOrder?: boolean;
    name: string;
  };

const FormListSelection = ({
  options,
  hideOrder,
  name,
  variant = 'filled',
  setFieldValue,
  errorProps,
  error,
  resultContainerProps,
  value: resultItems,
  ...props
}: FormListSelectionProps) => {
  const [selectedItems, setSelectedItems] = useState<IOption[]>([]);

  const optionValues = options.map(({ value }) => value);
  const selectedValues = selectedItems.map(({ value }) => value);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedItems(
      [...event.target.selectedOptions].map(({ value, label }) => ({
        value,
        label,
      }))
    );

  const handleMoveItems = (direction: Direction) => {
    const isUp = direction === Direction.Up;
    const reOrderedItems = selectedValues
      .sort((a, b) => optionValues.indexOf(isUp ? a : b) - optionValues.indexOf(isUp ? b : a))
      .reduce((acc, currentValue) => {
        const oldIndex = acc.indexOf(currentValue);

        const newLocation = isUp ? -1 : 1;

        const newIndex = oldIndex + newLocation;

        // Don't move if it's already at the bottom or the slot it's moving into is occupied by a selected item.

        const isBottom =
          direction === Direction.Down &&
          (oldIndex === acc.length || (acc[newIndex] && selectedValues.includes(acc[newIndex])));

        // Don't move if it's already at the top or the slot it's moving into is occupied by a selected item.

        const isTop = (isUp && oldIndex === 0) || selectedValues.includes(acc[newIndex]);

        if (isBottom || isTop) return acc;

        return arrayMove(acc, oldIndex, newIndex);
      }, resultItems as string[]);

    setFieldValue(name, reOrderedItems);
  };

  const renderSortableList =
    Array.isArray(resultItems) && resultItems.length > 0 ? (
      <FormListSelectionBox
        {...props}
        name={`${name}-result`}
        data-testid="form-list-selection-result"
        onChange={handleChange}
        variant={variant}
        options={options
          .filter(({ value }) => resultItems.includes(value))
          .sort((a, b) => resultItems.indexOf(a.value) - resultItems.indexOf(b.value))}
      />
    ) : (
      <Flex h="full" alignItems="center" justifyContent="center">
        <Text>No results.</Text>
      </Flex>
    );

  const renderOrderButtons = !hideOrder ? (
    <Flex flexDir="column">
      <IconButton
        aria-label="Move up"
        title="Move up"
        onClick={() => handleMoveItems(Direction.Up)}
        icon={<FaAngleUp />}
        mb={2}
      />
      <IconButton
        aria-label="Move down"
        title="Move down"
        onClick={() => handleMoveItems(Direction.Down)}
        icon={<FaAngleDown />}
        mb={2}
      />
    </Flex>
  ) : null;

  return (
    <Box w="full" flex={1} h="full">
      <Flex w="full" h="full" gap={6}>
        <FormListSelectionBox
          {...props}
          name={name}
          data-testid="form-list-selection-pool"
          onChange={handleChange}
          variant={variant}
          options={options
            .filter(({ value }) => !Array.isArray(resultItems) || !resultItems.includes(value))
            .sort((a, b) => optionValues.indexOf(a.value) - optionValues.indexOf(b.value))}
        />

        <Flex flexDir="column">
          <IconButton
            aria-label="Add item(s)"
            title="Add item(s)"
            onClick={() => {
              setFieldValue(name, addItems<string>(resultItems as string[], selectedValues, false));
              setSelectedItems([]);
            }}
            icon={<FaAngleRight />}
            mb={2}
          />

          <IconButton
            aria-label="Add all items"
            title="Add all items"
            onClick={() => {
              setFieldValue(
                name,
                addItems<string>(
                  resultItems as string[],
                  options
                    .filter(({ value }) => Array.isArray(resultItems) && !resultItems.includes(value))
                    .map(({ value }) => value),
                  false
                )
              );
              setSelectedItems([]);
            }}
            icon={<FaAngleDoubleRight />}
            mb={3}
          />
          <IconButton
            aria-label="Remove item(s)"
            title="Remove item(s)"
            onClick={() => {
              setFieldValue(name, deleteItems<string>(resultItems as string[], selectedValues));
              setSelectedItems([]);
            }}
            icon={<FaAngleLeft />}
            mb={2}
          />

          <IconButton
            aria-label="Remove all items"
            title="Remove all items"
            onClick={() => {
              setFieldValue(name, []);

              setSelectedItems([]);
            }}
            icon={<FaAngleDoubleLeft />}
          />
        </Flex>
        <Box h="full" bgColor="gray.100" {...resultContainerProps}>
          {renderSortableList}
        </Box>
        {renderOrderButtons}
      </Flex>
      <FormErrorMessage {...errorProps}>{error}</FormErrorMessage>
    </Box>
  );
};

export { FormListSelection };
